import { type SubmitEvent as ReactSubmitEvent, type FormEvent, memo, useState } from "react";
import { Card, type SelectOption, Select, SettingsLayout, Switch } from "@clintonelec/react-storybook";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import { selectTamperEvent, setTamperEventAction } from "Data/Redux/Slices/Settings/Event/Tamper";
import "Components/ConfigLayout/Event/TamperEventSettings/TamperEventSettings.less";
import ActionButtons from "Components/ConfigLayout/ActionButtons";
import { isEqual } from "lodash";
import { produce } from "immer";

type TamperEventSettingsForm = {
	eventName: HTMLInputElement;
	alarmOut: HTMLInputElement;
	snapshot: HTMLInputElement;
	whiteLight: HTMLInputElement;
	redBlueLight: HTMLInputElement;
	audioAlarm: HTMLInputElement;
	recording: HTMLInputElement;
	email: HTMLInputElement;
	ftp: HTMLInputElement;
} & HTMLFormElement;

const intervalSelectOptions: SelectOption[] = [ 5, 10, 15, 20, 25, 30 ].map(value => ({
	label: `${ value }`,
	value
}));

function EventTamperSettings() {
	const dispatch = useAppDispatch();
	const tamperEventSettings = useAppSelector(selectTamperEvent);
	const [ localTamperEventSettings, setLocalTamperEventSettings ] = useState(tamperEventSettings);
	const setTamperEventSettings = () => dispatch(setTamperEventAction(localTamperEventSettings));
	const disabled = isEqual(localTamperEventSettings, tamperEventSettings);
	const {
		interval, alarmOut, snapshot, whiteLight, redBlueLight, audioAlarm, recording, email, ftp
	} = localTamperEventSettings;

	const handleAlarmOutChange = (newEnabled: boolean) => {
		setLocalTamperEventSettings(produce(localTamperEventSettings, draft => {
			draft.alarmOut = newEnabled;
		}));
	};

	const handleSnapshotChange = (newEnabled: boolean) => {
		setLocalTamperEventSettings(produce(localTamperEventSettings, draft => {
			draft.snapshot = newEnabled;
		}));
	};

	const handleWhiteLightChange = (newEnabled: boolean) => {
		setLocalTamperEventSettings(produce(localTamperEventSettings, draft => {
			draft.whiteLight = newEnabled;
		}));
	};

	const handleRedBlueLightChange = (newEnabled: boolean) => {
		setLocalTamperEventSettings(produce(localTamperEventSettings, draft => {
			draft.redBlueLight = newEnabled;
		}));
	};

	const handleAudioAlarmChange = (newEnabled: boolean) => {
		setLocalTamperEventSettings(produce(localTamperEventSettings, draft => {
			draft.audioAlarm = newEnabled;
		}));
	};

	const handleRecordingChange = (newEnabled: boolean) => {
		setLocalTamperEventSettings(produce(localTamperEventSettings, draft => {
			draft.recording = newEnabled;
		}));
	};

	const handleEmailChange = (newEnabled: boolean) => {
		setLocalTamperEventSettings(produce(localTamperEventSettings, draft => {
			draft.email = newEnabled;
		}));
	};

	const handleFtpChange = (newEnabled: boolean) => {
		setLocalTamperEventSettings(produce(localTamperEventSettings, draft => {
			draft.ftp = newEnabled;
		}));
	};

	const handleFormSubmit = (event: ReactSubmitEvent<TamperEventSettingsForm>) => {
		event.preventDefault();

		if (event.currentTarget?.checkValidity()) {
			setTamperEventSettings();
		}
	};

	const handleFormReset = (event: FormEvent<TamperEventSettingsForm>) => {
		event.preventDefault();

		setLocalTamperEventSettings(tamperEventSettings);
	};

	const handleIntervalSelect = (newInterval: string | null) => {
		if (!newInterval) {
			return;
		}

		const newLocalState = produce(localTamperEventSettings, draft => {
			draft.interval = +newInterval;
		});

		setLocalTamperEventSettings(newLocalState);
	};

	return (
		<SettingsLayout.Form
			className="event-tamper-settings"
			noValidate
			onReset={ handleFormReset }
			onSubmit={ handleFormSubmit }
		>
			<SettingsLayout.ScrollableContainer>
				<Card size="medium" title="Tamper Event Settings">
					<div className="event-tamper-content">
						<div className="form-row">
							<span>Ignoring Interval</span>
							<Select
								allowClear={ false }
								className="form-select"
								onSelect={ handleIntervalSelect }
								options={ intervalSelectOptions }
								value={ interval }
							/>
						</div>
						<div className="form-row">
							<span>Alarm Out</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleAlarmOutChange } checked={ alarmOut } />
							</div>
						</div>
						<div className="form-row">
							<span>Snapshot</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleSnapshotChange } checked={ snapshot } />
							</div>
						</div>
						<div className="form-row">
							<span>White Light</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleWhiteLightChange }checked={ whiteLight } />
							</div>
						</div>
						<div className="form-row">
							<span>Red & Blue Light</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleRedBlueLightChange } checked={ redBlueLight } />
							</div>
						</div>
						<div className="form-row">
							<span>Audio Alarm</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleAudioAlarmChange } checked={ audioAlarm } />
							</div>
						</div>
						<div className="form-row">
							<span>Recording</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleRecordingChange } checked={ recording } />
							</div>
						</div>
						<div className="form-row">
							<span>E-Mail</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleEmailChange } checked={ email } />
							</div>
						</div>
						<div className="form-row">
							<span>FTP</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleFtpChange } checked={ ftp } />
							</div>
						</div>
					</div>
				</Card>
			</SettingsLayout.ScrollableContainer>
			<ActionButtons disabled={ disabled } />
		</SettingsLayout.Form>
	);
}

export default memo(EventTamperSettings);
